/**
 * @fileoverview Gallagher Insurance Field Definitions
 *
 * Purpose: Shared Gallagher field schema used by both the backend (seeding)
 * and the frontend (field display). These are the fields for the Gallagher
 * Insolvency Practitioner Appointment Notification Form (web portal).
 */

export interface GallagherField {
  key: string;
  label: string;
  description?: string;
  fieldType: string;
  canonicalKey?: string;
}

// Labels mirror the live AJG portal form verbatim (wording, casing and
// punctuation) so the same field reads identically in Morgan's preview, the
// portal-fields config, the generation wizard and the actual portal. Do not
// "tidy up" these strings — they are intentionally a 1:1 copy of the form.
// NOTE: this list is duplicated in src/lib/precedent-templates.ts (used by the
// frontend UI). Keep the two in sync.
export const GALLAGHER_INSURANCE_FIELDS: GallagherField[] = [
  {
    key: "appointment_date",
    label: "1. Date of appointment (dd/mm/yyyy)",
    fieldType: "date",
    canonicalKey: "appointment_date",
  },
  {
    key: "firm_name",
    label: "2. Insolvency practitioner (IP) firm",
    fieldType: "text",
    canonicalKey: "firm_name",
  },
  {
    key: "practitioner_name",
    label: "3. Name of appointees",
    fieldType: "text",
    canonicalKey: "practitioner_name",
  },
  {
    key: "contact_name",
    label: "4. IP file manager or primary contact",
    fieldType: "text",
    canonicalKey: "contact_name",
  },
  {
    key: "contact_email",
    label: "4a. Email",
    fieldType: "email",
    canonicalKey: "contact_email",
  },
  {
    key: "firm_phone",
    label: "4b. Direct telephone number",
    fieldType: "phone",
    canonicalKey: "contact_phone",
  },
  {
    key: "mobile_phone",
    label: "4c. Mobile telephone number",
    fieldType: "phone",
    canonicalKey: "mobile_phone",
  },
  {
    key: "administrator_type",
    label: "5. Type Of Appointment",
    fieldType: "text",
    canonicalKey: "administrator_type",
  },
  {
    key: "company_name",
    label: "6. Appointment name",
    fieldType: "text",
    canonicalKey: "appointment_name",
  },
  {
    key: "acn",
    label: "7. Australian/New Zealand Company Number – whichever is applicable",
    fieldType: "text",
    canonicalKey: "acn",
  },
  {
    key: "business_industry",
    label: "8a. What is the Business/ Industry/ Occupation of the Appointment?",
    fieldType: "text",
    canonicalKey: "industry_division",
  },
  {
    key: "business_activities",
    label:
      "8b. Please describe the Activities of the Business/ Industry/ Occupation being managed under the Appointment",
    fieldType: "text",
    canonicalKey: "business_activities",
  },
  {
    key: "general_comments",
    label: "8c. General Comments",
    fieldType: "text",
    canonicalKey: "general_comments",
  },
  {
    key: "principal_risk_address",
    label: "9. Principal risk address",
    fieldType: "address",
    canonicalKey: "registered_office_address",
  },
  {
    key: "trading_status",
    label: "10. Trading status",
    description: "Trading / Not Trading / Ceased Trading",
    fieldType: "text",
  },
  {
    key: "employing_status",
    label: "11. Employing status",
    description: "Employing / Not Employing",
    fieldType: "text",
  },
  {
    key: "locations_by_state",
    label: "12. Locations by state",
    description: "Comma-separated: ACT, NSW, NT, QLD, SA, TAS, VIC, WA, NZ",
    fieldType: "text",
    canonicalKey: "locations_by_state",
  },
  {
    key: "num_employees",
    label: "13. Number of employees or contractors",
    fieldType: "text",
  },
  {
    key: "estimated_asset_value",
    label: "14. Estimated asset value",
    fieldType: "text",
  },
  {
    key: "estimated_appointment_period",
    label: "15. Estimated appointment period",
    fieldType: "text",
  },
  {
    key: "estimated_turnover",
    label: "16. Estimated turnover during appointment",
    fieldType: "text",
  },
  {
    key: "pre_appointment_insurer",
    label: "17. Pre appointment insurer or broker",
    fieldType: "text",
  },
  {
    key: "pre_appointment_workcover",
    label: "18. Pre appointment WorkCover insurer (Australia Only)",
    fieldType: "text",
  },
  {
    key: "crucial_compliance",
    label: "Crucial Compliance Requirement",
    description:
      "Do you require assistance with Health & Safety, Environmental &/or Workplace Relations? (Yes/No)",
    fieldType: "text",
  },
  {
    key: "nominee_1_name",
    label: "Nominee 1 — Name",
    description: "Additional recipient of this notification",
    fieldType: "text",
  },
  { key: "nominee_1_email", label: "Nominee 1 — Email", fieldType: "email" },
  { key: "nominee_2_name", label: "Nominee 2 — Name", fieldType: "text" },
  { key: "nominee_2_email", label: "Nominee 2 — Email", fieldType: "email" },
];
